import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { Handle, Position, useReactFlow, useUpdateNodeInternals } from 'reactflow';
import { FaBolt, FaUser, FaFistRaised, // For Strength
FaRunning, // For Agility
FaEye, // For Perception
FaBrain, // For Intellect
FaShieldAlt // For Defence
 } from 'react-icons/fa';
import { DialogueRewardLevel } from '../../../api/models';
import { AvatarAbility, backendUrl } from '@common/contracts';
// Create a context to pass availableNPCs into nodes
export const NPCsContext = React.createContext([]);
/**
 * Component responsible for rendering 'npc' type nodes (NPC dialogue) in ReactFlow. It renders the NPC's line of text,
 * the NPC image and the player's response options. Response options are shown as separate blocks with `Handle` connectors
 * for creating links to subsequent scenario nodes. Supports rendering a background image and ability/reward icons
 * for the response options.
 */
export const NpcNode = ({ data, id, selected }) => {
    var _a, _b;
    // Get availableNPCs from context
    const availableNPCs = React.useContext(NPCsContext);
    // Find the selected NPC by ID
    const selectedNPC = data.selectedNPCId ? availableNPCs.find(npc => npc.id === data.selectedNPCId) : null;
    // Get the image from the selected NPC or fall back to the old npcImage field for backward compatibility
    const rawNpcImage = (selectedNPC ? selectedNPC.image : null) || data.npcImage;
    const npcImageUrl = rawNpcImage && !rawNpcImage.startsWith('http') ? `${backendUrl}${rawNpcImage}` : rawNpcImage;
    // Background normalization
    const rawBg = (_a = data.backgroundImage) !== null && _a !== void 0 ? _a : null;
    const normalizedBg = rawBg && !rawBg.startsWith('http') ? `${backendUrl}${rawBg}` : rawBg;
    // Get all edges to determine which handles are used
    const { getEdges } = useReactFlow();
    const edges = getEdges();
    // Determine which handles have incoming connections
    const incomingConnections = edges.filter((edge) => edge.target === id);
    const hasTopConnection = incomingConnections.some((edge) => edge.targetHandle === 'target-top');
    const hasLeftConnection = incomingConnections.some((edge) => edge.targetHandle === 'target-left');
    const hasRightConnection = incomingConnections.some((edge) => edge.targetHandle === 'target-right');
    // Determine active handles: if there are connections, only the connected one is active, otherwise all are active
    const hasAnyConnection = incomingConnections.length > 0;
    const topHandleActive = !hasAnyConnection || hasTopConnection;
    const leftHandleActive = !hasAnyConnection || hasLeftConnection;
    const rightHandleActive = !hasAnyConnection || hasRightConnection;
    // The `useUpdateNodeInternals` hook is meant to force an update of a node's internal data, such as
    // the position and size of its Handles. This is necessary when the node's content or structure changes dynamically in a way
    // that `reactflow` cannot automatically detect that it needs to recompute handle positions or update connections (edges).
    // The NpcNode component renders the player's response options based on the data passed via the `data.options` prop.
    // In the scenario editor the user can add or remove response options for an NPC node through the side panel -
    // these actions directly change the `data.options` array for the selected node. Using `useUpdateNodeInternals`
    // guarantees that `reactflow` is notified about the change in the number of output Handles on the NPC node every time
    // a response option is added or removed. Without this call `reactflow` might not recognize the new or removed Handles,
    // which would lead to incorrect behavior when trying to create or delete connections with these Handles,
    const updateNodeInternals = useUpdateNodeInternals();
    // Dependencies:
    // 1. Number of options - directly affects the node size and handle positions, requiring a recompute.
    // 2. The `updateNodeInternals` function requires the node Id to update, and if the node Id changes for some reason the effect must be
    // re-applied.
    // 3. Handle activity - when the handle active state changes, ReactFlow needs to be updated
    useEffect(() => {
        updateNodeInternals(id);
    }, [(_b = data.options) === null || _b === void 0 ? void 0 : _b.length, id, updateNodeInternals, topHandleActive, leftHandleActive, rightHandleActive]);
    // Function to get the ability icon
    const getCharacteristicIcon = (characteristic) => {
        switch (characteristic) {
            case AvatarAbility.Strength:
                return _jsx(FaFistRaised, {}); // Fist icon for strength
            case AvatarAbility.Agility:
                return _jsx(FaRunning, {}); // Running icon for agility
            case AvatarAbility.Perception:
                return _jsx(FaEye, {}); // Eye icon for perception
            case AvatarAbility.Intellect:
                return _jsx(FaBrain, {}); // Brain icon for intellect
            case AvatarAbility.Defence:
                return _jsx(FaShieldAlt, {}); // Shield icon for defence
            default:
                return _jsx(FaBolt, {}); // Default placeholder
        }
    };
    // Function to get the display text for the reward level
    const getRewardLevelText = (rewardLevel) => {
        switch (rewardLevel) {
            case DialogueRewardLevel.None:
                return 'NO REWARD';
            case DialogueRewardLevel.Low:
                return 'SMALL REWARD';
            case DialogueRewardLevel.Medium:
                return 'MEDIUM REWARD';
            case DialogueRewardLevel.High:
                return 'LARGE REWARD';
            default:
                return 'NO REWARD';
        }
    };
    // Function to get the CSS class for the reward level
    const getRewardLevelClass = (rewardLevel) => {
        switch (rewardLevel) {
            case DialogueRewardLevel.None:
                return 'no-reward';
            case DialogueRewardLevel.Low:
                return 'small-reward';
            case DialogueRewardLevel.Medium:
                return 'medium-reward';
            case DialogueRewardLevel.High:
                return 'large-reward';
            default:
                return 'no-reward';
        }
    };
    return (_jsxs("div", { className: `scenario-node npc-node ${selected ? 'selected' : ''} ${normalizedBg ? 'with-background' : ''}`, children: [_jsxs("div", { className: "node-type-badge npc-badge", children: [_jsx(FaUser, {}), " ", data.isRoot ? 'NPC Root' : 'NPC'] }), _jsx(Handle, { type: "target", position: Position.Top, id: "target-top", className: `unified-target-handle ${topHandleActive ? 'active' : 'inactive'}` }), _jsx(Handle, { type: "target", position: Position.Left, id: "target-left", className: `unified-target-handle ${leftHandleActive ? 'active' : 'inactive'}` }), _jsx(Handle, { type: "target", position: Position.Right, id: "target-right", className: `unified-target-handle ${rightHandleActive ? 'active' : 'inactive'}` }), normalizedBg && (_jsx("div", { className: "dialogue-background-image", style: {
                    '--dialogue-background-image': `url(${normalizedBg})`
                } })), _jsx("div", { className: "dialogue-center-container", children: _jsxs("div", { className: "dialogue-content-overlay", children: [npcImageUrl && (_jsx("div", { className: "npc-character-container", children: _jsx("img", { src: npcImageUrl, alt: "NPC Character", className: "npc-character-image" }) })), _jsx("div", { className: "npc-dialogue-bubble", children: _jsx("div", { className: "dialogue-text", children: data.text }) }), data.options && data.options.length > 0 && (_jsx("div", { className: "response-options-container", children: data.options.map((option, _index) => (_jsxs("div", { className: "response-option-button", children: [_jsxs("div", { className: "option-content", children: [_jsx("div", { className: "option-left", children: option.ability && (_jsx("div", { className: `characteristic-icon ${option.ability.toLowerCase()}`, children: getCharacteristicIcon(option.ability) })) }), _jsxs("div", { className: "option-center", children: [_jsx("span", { className: "option-text", children: option.text }), _jsxs("div", { className: "option-stats", children: [option.ability && option.requiredValue && (_jsxs("span", { className: `required-value ${option.ability.toLowerCase()}`, children: [option.ability, ": ", option.requiredValue] })), option.rewardLevel && option.rewardLevel !== DialogueRewardLevel.None && (_jsx("span", { className: `reward-badge ${getRewardLevelClass(option.rewardLevel)}`, children: getRewardLevelText(option.rewardLevel) }))] })] })] }), _jsx(Handle, { type: "source", position: Position.Right, id: `response-${option.id}`, className: "response-handle" })] }, option.id))) }))] }) })] }));
};
